// w09 Saodat — baroque-gold variant (full guest page).
// MAXIMALIST royal palace wedding guest page. Heavy gilded cartouches and
// ornaments live in the HERO; post-hero we keep things calm and let the
// shared sections breathe. A low warm ambient pad (F2/A2/C3/F3) plays
// under the page when the guest taps the floating music toggle.
//
// Signature: GOLD-FOIL UNROLL — a horizontal gilt sweep animates across
// the hero names on first load. linear-gradient(gold→cream→gold), animated
// background-position, background-clip:text. 1.6s ease. Reduced-motion
// users see a static foil fill.
//
// Sounds (3): swoosh on hero foil reveal, chime on RSVP yes, click on
// schedule item taps.

import { useEffect, useMemo, useRef, useState } from "react";
import type { VariantProps } from "./types";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";

// ─── 7-step schedule ─────────────────────────────────────────────────────
const SCHEDULE: Array<[string, string]> = [
  ["16:00", "Mehmonlarni kutib olish — qabulxona va aperitiv"],
  ["17:00", "Nikoh marosimi — Magnum Palace bosh zali"],
  ["17:30", "Fotosurat — bog' va ayvonda kuyov-kelin bilan"],
  ["18:30", "Oqshom dasturxoni — palov, somsa, manti"],
  ["20:00", "Birinchi raqs va mehmonlar raqsi"],
  ["22:00", "Tort kesish — uch qavatli oltin tort"],
  ["23:30", "Yakuniy raqs va mehmonlarni kuzatish"],
];

// ─── Reusable tiny SVG flourish ──────────────────────────────────────────
function GoldFlourish({ color, width = 160 }: { color: string; width?: number }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 160 24"
      style={{ width, height: width * (24 / 160), color }}
    >
      <path
        d="M2 12 C 28 4 48 20 80 12 C 112 4 132 20 158 12"
        fill="none"
        stroke={color}
        strokeWidth="1"
      />
      <path
        d="M10 12 C 28 8 48 16 80 12 C 112 8 132 16 150 12"
        fill="none"
        stroke={color}
        strokeWidth="0.6"
        opacity="0.7"
      />
      <circle cx="80" cy="12" r="2.6" fill={color} />
      <circle cx="2" cy="12" r="1.6" fill={color} />
      <circle cx="158" cy="12" r="1.6" fill={color} />
      <path
        d="M70 12 L 80 6 L 90 12 L 80 18 Z"
        fill={color}
        opacity="0.5"
      />
    </svg>
  );
}

// ─── Decorative corner used at section edges ─────────────────────────────
function GoldCorner({ color, size = 64 }: { color: string; size?: number }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 80 80"
      style={{ width: size, height: size, color }}
    >
      <path d="M2 78 L 2 14 C 2 8 8 2 14 2 L 78 2" fill="none" stroke={color} strokeWidth="1" />
      <path
        d="M14 14 C 28 14 14 28 14 42 C 14 52 22 60 36 60 C 50 60 60 50 60 36 C 60 22 50 14 36 14 Z"
        fill="none"
        stroke={color}
        strokeWidth="0.8"
        opacity="0.7"
      />
      <circle cx="14" cy="14" r="2.4" fill={color} />
      <circle cx="36" cy="36" r="1.6" fill={color} />
    </svg>
  );
}

// ─── Reveal on intersection ──────────────────────────────────────────────
function Reveal({ children, reduce }: { children: React.ReactNode; reduce: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: reduce ? "none" : "opacity .9s ease-out, transform .9s ease-out",
      }}
    >
      {children}
    </div>
  );
}

// ─── Ambient frequency spec — low warm royal pad (F2/A2/C3/F3) ──────────
const BAROQUE_AMBIENT = {
  freqs: [87.31, 110.0, 130.81, 174.61],
  type: "sine" as const,
  master: 0.04,
  lfo: 0.08,
};

export function BaroqueGoldVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();

  const heroPhoto = t.photos[0];
  const gold = t.accent;

  // ─── Signature: GOLD-FOIL UNROLL ────────────────────────────────────────
  const [foilPlayed, setFoilPlayed] = useState(false);
  useEffect(() => {
    if (reduce) return;
    const id = window.setTimeout(() => {
      play("swoosh");
      setFoilPlayed(true);
    }, 250);
    return () => window.clearTimeout(id);
  }, [reduce, play]);

  // ─── Ambient music toggle ──────────────────────────────────────────────
  const [music, setMusic] = useState(false);
  useAmbient(music, BAROQUE_AMBIENT);

  // ─── Foil animation styles ─────────────────────────────────────────────
  const foilStyle = useMemo(() => {
    const grad = `linear-gradient(100deg, ${gold} 0%, #F1D77A 30%, #FBF2D6 50%, #F1D77A 70%, ${gold} 100%)`;
    if (reduce) {
      return {
        backgroundImage: grad,
        backgroundSize: "100% 100%",
        backgroundPosition: "50% 50%",
        WebkitBackgroundClip: "text" as const,
        backgroundClip: "text" as const,
        color: "transparent",
      };
    }
    return {
      backgroundImage: grad,
      backgroundSize: "300% 100%",
      backgroundPosition: foilPlayed ? "0% 50%" : "100% 50%",
      transition: "background-position 1.6s ease",
      WebkitBackgroundClip: "text" as const,
      backgroundClip: "text" as const,
      color: "transparent",
    };
  }, [gold, foilPlayed, reduce]);

  return (
    <div className="v-w09">
      <style>{`
        @media (max-width: 768px) {
          .v-w09 .v-w09-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .v-w09 .v-w09-hero h1 { font-size: clamp(36px, 12vw, 72px) !important; }
          .v-w09 .v-w09-hero-content { max-width: 100% !important; }
          .v-w09 .v-w09-music-btn { min-height: 44px; min-width: 44px; }
          /* Scale corner ornaments down so they don't crowd the names on 390px */
          .v-w09 .v-w09-corner { width: 48px !important; height: 48px !important; }
          /* Date/venue lines: allow wrapping on narrow screens */
          .v-w09 .v-w09-hero .v-w09-venue {
            font-size: 10px !important;
            letter-spacing: 0.28em !important;
            word-break: break-word !important;
            white-space: normal !important;
          }
        }
        .v-w09 button:focus-visible { outline: 2px solid ${gold}; outline-offset: 2px; }
      `}</style>
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        className="hero-fill v-w09-hero"
        style={{
          minHeight: "100vh",
          padding: "100px 24px 80px",
          background: t.bg,
          color: t.ink,
          position: "relative",
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
        }}
      >
        {heroPhoto && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${heroPhoto}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "sepia(0.25) saturate(0.85) brightness(0.85)",
            }}
          />
        )}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(170deg, ${t.bg}EE 0%, ${gold}33 40%, ${t.bg}EE 100%)`,
          }}
        />
        <div className="v-w09-corner" style={{ position: "absolute", top: 16, left: 16, color: gold }}>
          <GoldCorner color={gold} size={88} />
        </div>
        <div
          className="v-w09-corner"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            transform: "scaleX(-1)",
            color: gold,
          }}
        >
          <GoldCorner color={gold} size={88} />
        </div>
        <div
          className="v-w09-corner"
          style={{
            position: "absolute",
            bottom: 16,
            left: 16,
            transform: "scaleY(-1)",
            color: gold,
          }}
        >
          <GoldCorner color={gold} size={88} />
        </div>
        <div
          className="v-w09-corner"
          style={{
            position: "absolute",
            bottom: 16,
            right: 16,
            transform: "scale(-1, -1)",
            color: gold,
          }}
        >
          <GoldCorner color={gold} size={88} />
        </div>

        <div className="v-w09-hero-content" style={{ position: "relative", zIndex: 1, maxWidth: 920 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: gold,
              marginBottom: 22,
            }}
          >
            NIKOH MAROSIMI · 2026
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <svg width="56" height="42" viewBox="0 0 56 42" aria-hidden style={{ color: gold }}>
              <path
                d="M4 32 L 10 12 L 18 26 L 28 6 L 38 26 L 46 12 L 52 32 Z"
                fill={gold}
                stroke={t.ink}
                strokeWidth="0.6"
              />
              <rect x="4" y="32" width="48" height="6" fill={gold} />
              <circle cx="10" cy="14" r="2" fill={t.ink} />
              <circle cx="28" cy="8" r="2.4" fill={t.ink} />
              <circle cx="46" cy="14" r="2" fill={t.ink} />
            </svg>
          </div>

          <h1
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "clamp(56px, 12vw, 132px)",
              lineHeight: 0.95,
              margin: 0,
              ...foilStyle,
            }}
          >
            {t.names}
          </h1>

          <div
            style={{
              marginTop: 28,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              color: gold,
            }}
          >
            <GoldFlourish color={gold} width={140} />
          </div>

          <div
            style={{
              marginTop: 18,
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(20px, 3vw, 28px)",
              color: t.ink,
            }}
          >
            {t.date}
          </div>
          <div
            className="v-w09-venue"
            style={{
              marginTop: 6,
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: gold,
            }}
          >
            {t.venue.toUpperCase()}
          </div>
        </div>
      </section>

      {/* ── COUNTDOWN ─────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ── STORY (trimmed) ───────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="Bizning hikoyamiz"
          title="Klassik musiqa va do'stning to'yi"
          body="Biz bir-birimizni do'stimizning to'yida — Magnum Palace zalida — Vivaldining 'Fasllar' kuylari ostida uchratdik. Bir yildan so'ng, xuddi shu zalning ayvonida, orkestr 'Pachelbel kanonini' chalganda Otabek tiz cho'kdi. Bugun sizlarni hayotimizning eng go'zal kuyiga taklif qilamiz."
        />
      </Reveal>

      {/* ── GALLERY ───────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      {/* ── SCHEDULE (with click sound) ──────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div onClickCapture={(e) => {
          const target = e.target as HTMLElement | null;
          if (!target) return;
          if (target.closest("button")) return;
          play("click");
        }}>
          <ScheduleSection t={t} items={SCHEDULE} />
        </div>
      </Reveal>

      {/* ── MAP ─────────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine="Magnum Palace, Toshkent shahri, Yunusobod tumani"
        />
      </Reveal>

      {/* ── RSVP (with chime on yes) ─────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          onClickCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const btn = target.closest("button");
            if (btn && /yes|ha|✓|kelaman/i.test(btn.textContent || "")) {
              play("chime");
            }
          }}
        >
          <RsvpSection
            t={t}
            isDark={isDark}
            {...ctx.rsvp}
            slug={ctx.slug}
            label="Saodatli kunimizda biz bilan bo'ling"
          />
        </div>
      </Reveal>

      {/* ── SHARE ────────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <GuestFooter t={t} />

      {/* ── Floating ambient toggle ──────────────────────────────────────── */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((m) => !m)}
        accent={t.accent}
        ink={t.bg}
        style={{ bottom: 20, right: 20 }}
        className="v-w09-music-btn"
      />
    </div>
  );
}
